import React from 'react';
import EntityAuditHistory from './EntityAuditHistory';
import Button from '../common/Button';

function CompanyDetailModal({ company, onClose }) {
  if (!company) return null;

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return 'bg-[#e8efe5] text-[#4a6b3f]';
    if (s === 'dissolved' || s === 'struck off') return 'bg-[#f2e9e6] text-[#9a564a]';
    if (s === 'inactive') return 'bg-[#f3ebd7] text-[#8a6a1f]';
    return 'bg-[#edeef3] text-[#3a4a66]';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#4a6b3f';
    if (score >= 50) return '#b07a1a';
    return '#9a564a';
  };

  const formatCurrency = (val) => `\u20B9${(val || 0).toLocaleString('en-IN')}`;
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '-');

  const deadlines = company.complianceSnapshot?.deadlines || [];
  const directors = company.directors || [];
  const charges = company.charges || [];

  const InfoCell = ({ label, children }) => (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[1px] text-[#7a7060] mb-0.5">{label}</div>
      <div className="text-[12.5px] text-[#08111f]">{children || '-'}</div>
    </div>
  );

  const SectionTitle = ({ children }) => (
    <div className="text-[11px] font-bold uppercase tracking-[1px] text-[#7a7060] mb-2">{children}</div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(8,17,31,0.55)' }} onClick={onClose}>
      <div
        className="bg-white rounded-[12px] max-w-[700px] w-full max-h-[90vh] overflow-y-auto cs-content-scroll mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b" style={{ borderColor: '#ddd6c6', background: 'linear-gradient(180deg, #f8f5ef 0%, #ffffff 100%)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-[18px] font-bold text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {company.name}
              </h3>
              {company.cin && (
                <div className="text-[11px] text-[#7a7060] mt-0.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{company.cin}</div>
              )}
            </div>
            <button onClick={onClose} className="text-[20px] text-[#7a7060] hover:text-[#08111f]">&times;</button>
          </div>
          {/* Status + Score chips */}
          <div className="flex items-center gap-2 mt-2">
            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${getStatusBadge(company.status)}`}>
              {company.status || '-'}
            </span>
            {company.score != null && (
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${getScoreColor(company.score)}18`, color: getScoreColor(company.score) }}>
                Score: {company.score}
              </span>
            )}
            {company.type && (
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#145f5f22', color: '#145f5f' }}>
                {company.type}
              </span>
            )}
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Company Info */}
          <div>
            <SectionTitle>Company Information</SectionTitle>
            <div className="grid grid-cols-3 gap-x-4 gap-y-3">
              <InfoCell label="Category">{company.category}</InfoCell>
              <InfoCell label="Sub Category">{company.subCategory}</InfoCell>
              <InfoCell label="Class">{company.classOfCompany || company.class}</InfoCell>
              <InfoCell label="Incorporation Date">{formatDate(company.incorporated || company.incorporationDate)}</InfoCell>
              <InfoCell label="ROC">{company.roc}</InfoCell>
              <InfoCell label="Jurisdiction">{company.jurisdiction}</InfoCell>
              <InfoCell label="Listed">{typeof company.listed === 'string' ? company.listed : (company.listed != null ? (company.listed ? 'Yes' : 'No') : '-')}</InfoCell>
            </div>
          </div>

          {/* Capital */}
          <div className="rounded-[10px] border p-3 grid grid-cols-2 gap-4" style={{ borderColor: '#ddd6c6', background: '#faf8f4' }}>
            <InfoCell label="Authorized Capital">{formatCurrency(company.authorisedCapital || company.authorizedCapital)}</InfoCell>
            <InfoCell label="Paid-up Capital">{formatCurrency(company.paidUpCapital)}</InfoCell>
          </div>

          {/* Contact */}
          <div>
            <SectionTitle>Contact</SectionTitle>
            <div className="space-y-2">
              <InfoCell label="Email">{company.email}</InfoCell>
              <InfoCell label="Registered Address">{company.registeredAddress}</InfoCell>
              {company.booksAddress && <InfoCell label="Books Address">{company.booksAddress}</InfoCell>}
              {company.city && <InfoCell label="City">{company.city}</InfoCell>}
            </div>
          </div>

          {/* Compliance */}
          <div>
            <SectionTitle>Compliance</SectionTitle>
            <div className="grid grid-cols-3 gap-x-4 gap-y-3 mb-3">
              <InfoCell label="Active Compliance">{company.activeCompliance}</InfoCell>
              <InfoCell label="Financial Year End">{company.financialYearEnd}</InfoCell>
              <InfoCell label="Last AGM Date">{formatDate(company.lastAgm || company.lastAgmDate)}</InfoCell>
              <InfoCell label="Balance Sheet Date">{formatDate(company.balanceSheetDate)}</InfoCell>
            </div>

            {deadlines.length > 0 && (
              <div className="rounded-[10px] border overflow-hidden" style={{ borderColor: '#ddd6c6' }}>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr style={{ background: '#f8f5ef', borderBottom: '1px solid #ddd6c6' }}>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Form</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Due Date</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Status</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Days Left</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Penalty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deadlines.map((dl, idx) => (
                      <tr key={idx} className="transition-colors hover:bg-[#f8f5ef]" style={{ borderBottom: '1px solid #ddd6c6' }}>
                        <td className="py-2 px-3 font-medium text-[#08111f]">{dl.formCode || '-'}</td>
                        <td className="py-2 px-3 text-[#7a7060]">{formatDate(dl.dueDate)}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${
                            (dl.status || '').toLowerCase() === 'filed' ? 'bg-[#e8efe5] text-[#4a6b3f]' :
                            (dl.status || '').toLowerCase() === 'overdue' ? 'bg-[#f2e9e6] text-[#9a564a]' :
                            'bg-[#f3ebd7] text-[#8a6a1f]'
                          }`}>{dl.status || '-'}</span>
                        </td>
                        <td className="py-2 px-3 text-[#7a7060]">{dl.daysRemaining != null ? dl.daysRemaining : '-'}</td>
                        <td className="py-2 px-3 text-[#7a7060]">{dl.penalty != null ? `\u20B9${dl.penalty.toLocaleString('en-IN')}` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Directors */}
          <div>
            <SectionTitle>Directors ({directors.length})</SectionTitle>
            {directors.length > 0 ? (
              <div className="rounded-[10px] border overflow-hidden" style={{ borderColor: '#ddd6c6' }}>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr style={{ background: '#f8f5ef', borderBottom: '1px solid #ddd6c6' }}>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">DIN</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Name</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Designation</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Appointed</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Signatory</th>
                    </tr>
                  </thead>
                  <tbody>
                    {directors.map((dir, idx) => (
                      <tr key={idx} className="transition-colors hover:bg-[#f8f5ef]" style={{ borderBottom: '1px solid #ddd6c6' }}>
                        <td className="py-2 px-3 text-[#08111f]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{dir.din || '-'}</td>
                        <td className="py-2 px-3 text-[#08111f]">{dir.name || '-'}</td>
                        <td className="py-2 px-3 text-[#7a7060]">{dir.designation || '-'}</td>
                        <td className="py-2 px-3 text-[#7a7060]">{formatDate(dir.appointedDate)}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${
                            dir.signatory ? 'bg-[#e8efe5] text-[#4a6b3f]' : 'bg-[#edeef3] text-[#3a4a66]'
                          }`}>{dir.signatory ? 'Yes' : 'No'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-[12px] text-[#7a7060]">No directors on record</div>
            )}
          </div>

          {/* Charges */}
          {charges.length > 0 && (
            <div>
              <SectionTitle>Charges ({charges.length})</SectionTitle>
              <div className="rounded-[10px] border overflow-hidden" style={{ borderColor: '#ddd6c6' }}>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr style={{ background: '#f8f5ef', borderBottom: '1px solid #ddd6c6' }}>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Holder</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Amount</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Created</th>
                      <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-[1px] text-[#7a7060]">Satisfied</th>
                    </tr>
                  </thead>
                  <tbody>
                    {charges.map((charge, idx) => (
                      <tr key={idx} className="transition-colors hover:bg-[#f8f5ef]" style={{ borderBottom: '1px solid #ddd6c6' }}>
                        <td className="py-2 px-3 text-[#08111f]">{charge.holderName || '-'}</td>
                        <td className="py-2 px-3 text-[#7a7060]">{charge.amount != null ? `\u20B9${charge.amount.toLocaleString('en-IN')}` : '-'}</td>
                        <td className="py-2 px-3 text-[#7a7060]">{formatDate(charge.createdDate)}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${
                            charge.satisfied ? 'bg-[#e8efe5] text-[#4a6b3f]' : 'bg-[#f3ebd7] text-[#8a6a1f]'
                          }`}>{charge.satisfied ? 'Yes' : 'No'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Audit Trail — compact card */}
          <div className="rounded-[10px] border p-4" style={{ borderColor: '#ddd6c6', background: '#faf8f4' }}>
            <SectionTitle>Audit Trail</SectionTitle>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {company.createdBy?.fullName && <InfoCell label="Created By">{company.createdBy.fullName}</InfoCell>}
              {company.createdBy?.email && <InfoCell label="Email">{company.createdBy.email}</InfoCell>}
              {company.createdBy?.mobile && <InfoCell label="Mobile">{company.createdBy.mobile}</InfoCell>}
              {company.createdBy?.profession && <InfoCell label="Profession">{company.createdBy.profession}</InfoCell>}
              {company.createdBy?.membershipNo && <InfoCell label="Membership No">{company.createdBy.membershipNo}</InfoCell>}
              <InfoCell label="Created At">{formatDate(company.createdAt)}</InfoCell>
              <InfoCell label="Claimed By">
                {company.owner?.fullName ? (
                  <>
                    {company.owner.fullName}
                    {company.owner.email && (
                      <span className="text-[10px] text-[#7a7060] ml-1">({company.owner.email})</span>
                    )}
                  </>
                ) : (
                  <span className="text-[#9a5a00]">Unclaimed</span>
                )}
              </InfoCell>
              {company.claimedAt && <InfoCell label="Claimed At">{formatDate(company.claimedAt)}</InfoCell>}
              {company.lastModifiedBy && (
                <InfoCell label="Last Modified By">
                  {company.lastModifiedBy.fullName || '-'}
                  {company.lastModifiedBy.email && (
                    <span className="text-[10px] text-[#7a7060] ml-1">({company.lastModifiedBy.email})</span>
                  )}
                </InfoCell>
              )}
              {company.updatedAt && <InfoCell label="Last Modified At">{formatDate(company.updatedAt)}</InfoCell>}
            </div>
          </div>

          {/* Audit history for this company */}
          <EntityAuditHistory entityType="Company" entityId={company._id} limit={20} />

          {/* Footer */}
          <div className="flex justify-end pt-4 border-t" style={{ borderColor: '#ddd6c6' }}>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyDetailModal;
